/**
 * Операции звонка (изменение v1.14, BS 7.15) поверх типизированного клиента.
 *
 * Длинный опрос держит запрос до 10 с — меньше таймаута чтения клиента (15 с), поэтому
 * отдельной настройки времени ему не нужно.
 */
import { api, unwrap } from '@shared/api/client'
import { transportFetch } from '@shared/api/transport'
import type { Call, CallSignals, CallStart } from '@shared/api/types'

/** Сколько секунд сервер держит длинный опрос без изменений. */
export const CALL_POLL_WAIT_SEC = 10

export interface CallsApi {
  create(conversationId: string, clientCallId: string, commitment: string): Promise<CallStart>
  accept(callId: string, publicKey: string): Promise<CallStart>
  confirm(callId: string, publicKey: string): Promise<Call>
  decline(callId: string): Promise<Call>
  connected(callId: string): Promise<Call>
  end(callId: string, failed: boolean): Promise<Call>
  signal(callId: string, payload: string): Promise<number>
  poll(
    callId: string,
    after: number,
    version: number,
    wait: number,
    signal: AbortSignal,
  ): Promise<CallSignals>
  get(callId: string): Promise<Call>
  active(): Promise<Call | null>
}

export const callsApi: CallsApi = {
  async create(conversationId, clientCallId, commitment) {
    return unwrap(
      await api.POST('/calls', {
        body: { conversation_id: conversationId, client_call_id: clientCallId, key_commitment: commitment },
      }),
    )
  },
  async accept(callId, publicKey) {
    return unwrap(
      await api.POST('/calls/{callId}/accept', {
        params: { path: { callId } },
        body: { public_key: publicKey },
      }),
    )
  },
  async confirm(callId, publicKey) {
    return unwrap(
      await api.POST('/calls/{callId}/confirm', {
        params: { path: { callId } },
        body: { public_key: publicKey },
      }),
    )
  },
  async decline(callId) {
    return unwrap(await api.POST('/calls/{callId}/decline', { params: { path: { callId } } }))
  },
  async connected(callId) {
    return unwrap(await api.POST('/calls/{callId}/connected', { params: { path: { callId } } }))
  },
  async end(callId, failed) {
    return unwrap(
      await api.POST('/calls/{callId}/end', {
        params: { path: { callId } },
        body: failed ? { reason: 'failed' } : {},
      }),
    )
  },
  async signal(callId, payload) {
    return unwrap(
      await api.POST('/calls/{callId}/signals', { params: { path: { callId } }, body: { payload } }),
    ).seq
  },
  async poll(callId, after, version, wait, signal) {
    return unwrap(
      await api.GET('/calls/{callId}/signals', {
        params: { path: { callId }, query: { after, version, wait } },
        signal,
      }),
    )
  },
  async get(callId) {
    return unwrap(await api.GET('/calls/{callId}', { params: { path: { callId } } }))
  },
  async active() {
    const result = await api.GET('/calls/active')
    return result.response.status === 204 ? null : (result.data ?? null)
  },
}

/**
 * Завершение звонка при закрытии вкладки: `keepalive` переживает выгрузку документа. Идёт через
 * точку подмены транспорта — в демо его получает мок. Ошибки не важны: сервер сам завершит
 * звонок по сроку пульса.
 */
export function endCallOnUnload(callId: string): void {
  try {
    const request = new Request(`/api/v1/calls/${encodeURIComponent(callId)}/end`, {
      method: 'POST',
      keepalive: true,
      credentials: 'same-origin',
      headers: { 'X-Client': `web/${__APP_VERSION__}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: 'failed' }),
    })
    void transportFetch(request).catch(() => undefined)
  } catch {
    /* выгрузка уже идёт */
  }
}
